import * as ort from 'onnxruntime-web';

let wasmRuntimeConfigured = false;

/**
 * Detect available AI runtime device with WebGPU-first fallback.
 * @returns {Promise<{webgpuAvailable:boolean, wasmAvailable:boolean, device:'webgpu'|'wasm', error:string|null}>}
 */
export async function detectGPU() {
  const info = {
    webgpuAvailable: false,
    wasmAvailable: true,
    device: 'wasm',
    error: null,
  };

  try {
    if (navigator.gpu) {
      const adapter = await navigator.gpu.requestAdapter();
      if (adapter) {
        info.webgpuAvailable = true;
        info.device = 'webgpu';
        return info;
      }
    }
  } catch (error) {
    info.error = error?.message ?? 'Unknown GPU detection error';
    console.warn('WebGPU not available, falling back to WASM:', error);
  }

  return info;
}

export function getExecutionProvider(device) {
  return device === 'webgpu' ? ['webgpu'] : ['wasm'];
}

function isThreadingSupported() {
  if (typeof SharedArrayBuffer === 'undefined') return false;
  if (typeof crossOriginIsolated === 'boolean') return crossOriginIsolated;
  return false;
}

/**
 * Configure ONNX Runtime Web WASM backend for SIMD and threads.
 * Threads require SharedArrayBuffer + cross-origin isolation.
 * @param {{ force?: boolean, maxThreads?: number, simd?: boolean }} [options]
 * @returns {{ simd: boolean, numThreads: number, threadingEnabled: boolean }}
 */
export function configureOnnxWasmRuntime(options = {}) {
  if (wasmRuntimeConfigured && !options.force) {
    return {
      simd: ort.env.wasm.simd,
      numThreads: ort.env.wasm.numThreads,
      threadingEnabled: ort.env.wasm.numThreads > 1,
    };
  }

  const maxThreads = Number.isFinite(options.maxThreads) ? Math.max(1, Math.floor(options.maxThreads)) : 4;
  const hardwareThreads = typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency)
    ? Math.max(1, navigator.hardwareConcurrency)
    : 1;
  const threadingEnabled = isThreadingSupported();
  const numThreads = threadingEnabled ? Math.min(maxThreads, hardwareThreads) : 1;
  const simd = options.simd ?? true;

  ort.env.wasm.simd = simd;
  ort.env.wasm.numThreads = numThreads;

  wasmRuntimeConfigured = true;
  return { simd, numThreads, threadingEnabled };
}

